import { Component, OnInit, OnDestroy, ViewChild, HostListener, Input, Host, Output, EventEmitter } from '@angular/core';
import * as Chart from 'chart.js';
import * as ChartAnnotation from 'chartjs-plugin-annotation';
import { HistoryCreditStatusModel } from '../../../models/consumer/history-credit-status.model';
import { ConsumerService } from '../../../services/consumer.service';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { getMinY } from '../../../utilities/chart-utilities';
import { CreditStatusItemComponent } from '../grid-item/credit-status-item.component';
import { DataService } from '../../../services/data.service';


@Component({
    selector: 'credit-status-chart',
    template: '<canvas id="basechart" #basechart></canvas>',
    host: { 'class': 'block-detail' },
    styles: [`:host { width: 100%; position: relative; }`]
})
export class CreditStatusChartComponent implements OnInit, OnDestroy {

    @ViewChild('basechart', { static: true }) basechart: any;
    public data: HistoryCreditStatusModel[] = [];
    chart: any;
    ctx: any;
    public subscriptions: any[] = [];
    debounceTimeout = 2000;
    debounceId: any;
    consumerId: string;
    creditChartData: any[] = [];
    gradientStroke: any;
    timePeriod: number = 1;
    pipe: DatePipe = new DatePipe('en_GB');
    clientId: number;
    showLimit: boolean = false;
    emergencyCreditLimit: number = 0;
    @Input() hasData: boolean = false;
    @Output() hasDataChange = new EventEmitter<boolean>();

    @Input() hasLoading: boolean = true;
    @Output() hasLoadingChange = new EventEmitter<boolean>();

    constructor(private consumerService: ConsumerService,
        private route: ActivatedRoute,
        private router: Router,
        private dataService: DataService,
        @Host() private parent: CreditStatusItemComponent) {
        Chart.pluginService.register(ChartAnnotation);
    }

    ngOnInit() {
        this.clientId = this.route.parent.snapshot.params['clientId'];
        this.subscriptions.push(this.route
            .params
            .subscribe(params => {
                this.consumerId = params['consumerId'];
            }));

        this.subscriptions.push(this.route
            .queryParams
            .subscribe(queryParams => {
                if (+queryParams['timePeriod']) {
                    this.timePeriod = +queryParams['timePeriod'];
                }

                this.consumerService.getHistoryCreditStatus(this.clientId, this.consumerId, this.timePeriod).then((creditRes) => {
                    this.data = creditRes;                    
                    if (creditRes.length > 0) {
                        this.dataService.creditStatusItemComponentResolved.next(true);
                        this.parent.setExpandVisable(true);
                        this.emergencyCreditLimit = this.data[0].emergencyCreditLimit;
                        this.creditChartData = [];
                        this.creditChartData = this.data.map(item => {
                            return {
                                x: item.dateCreated.toString(),
                                y: item.creditStatus
                            };
                        });

                        let minY = getMinY(this.creditChartData);
                        this.showLimit = minY <= this.emergencyCreditLimit;

                        this.createChart();
                    } else {
                        this.dataService.creditStatusItemComponentResolved.next(false);
                        this.parent.setExpandVisable(false);
                    }
                });
            }));
    }

    ngOnDestroy() {
        if (this.debounceId) {
            clearTimeout(this.debounceId);
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: any) {
        this.refreshHistoryData();
    }

    private getChartConfig(): any {
        let config = {
            type: 'line',
            options: {
                legend: {
                    borderDash: [10, 6],
                    labels: {
                    }
                },
                annotation: {
                    annotations: [{
                        drawTime: 'afterDraw',
                        type: 'line',
                        scaleID: "y-axis-0",
                        mode: 'horizontal',
                        value: this.emergencyCreditLimit,
                        borderColor: '#C82B2B',
                        borderWidth: 2.5,
                        borderDash: [10, 6],
                    }]
                },
                maintainAspectRatio: true,
                steppedLine: true,
                tooltips: {
                    backgroundColor: 'rgba(255,255,255,255)',
                    titleFontColor: '#153954',
                    bodyFontColor: '#153954',
                    borderWidth: 2,
                    borderColor: '#009aa3',
                    callbacks: {
                        title: function (tooltipItem) {
                            let callbackDate = new Date(tooltipItem[0].xLabel).toString();
                            return new DatePipe('en-GB').transform(callbackDate, 'dd/MM/yy').toString();
                        }
                    }
                },
                scales: {
                    xAxes: [{
                        scaleLabel: {
                            display: true,
                            labelString: 'Date',
                            fontSize: 15,
                            fontStyle: "bold",
                        },
                        type: 'time',
                        displayFormats: {
                            'month': 'MM',
                        },
                        gridLines: {
                            display: false
                        },
                        time: {
                            unit: 'month',
                            unitStepSize: 1,
                            displayFormats: {
                                'month': 'MMM'
                            }
                        }
                    }],
                    yAxes: [
                        {
                            scaleLabel: {
                                display: true,
                                labelString: 'Value',
                                fontSize: 15,
                                fontStyle: "bold",
                            },
                            position: 'left',
                            gridLines: {
                                display: true
                            },
                            ticks: {
                                callback: (label: number, index: number, labels: any) => {
                                    // comma separated thousands regex
                                    return '£' + label.toString();
                                }
                            }
                        },
                    ]

                }
            }
        }

        return config;
    }

    refreshHistoryData() {

        let queryParams: any = JSON.parse(JSON.stringify(this.route.snapshot.queryParams)) as any;

        var newRoute = this.router.createUrlTree(['/portal/' + this.clientId + '/consumer', this.consumerId], { queryParams: queryParams });

        this.consumerService.getHistoryCreditStatus(this.clientId, this.consumerId, this.timePeriod).then((creditRes) => {
            this.data = creditRes;
            if (creditRes.length > 0) {
                this.creditChartData = this.data.map(item => {
                    return {
                        x: item.dateCreated.toString(),
                        y: item.creditStatus
                    };
                });

            }
            this.createChart();
        });
    }

    createChart() {
        if (this.chart != null) this.chart.destroy();
        if (!this.basechart) return;

        this.gradientStroke = this.basechart.nativeElement.getContext('2d').createLinearGradient(500, 0, 100, 0);
        this.gradientStroke.addColorStop(0, '#153954');
        this.gradientStroke.addColorStop(1, '#00539f');

        let datasets = [] as any;
        let data = [] as any;
        if (this.creditChartData.length > 0) {
            data = this.creditChartData;
        } else {
            data = [];
        }

        datasets.push({
            label: 'Credit status',
            lineTension: 0.2,
            data: data,
            spanGaps: true,
            backgroundColor: '#153954',
            fill: false,
            pointRadius: 3,
            borderWidth: 2,
            pointHoverRadius: 5,
            pointHitRadius: 10,
            borderColor: '#153954',

            pointBorderColor: '#153954',
            pointBackgroundColor: '#153954',
            pointHoverBackgroundColor: '#153954',
            pointHoverBorderColor: '#153954',
            legend: {
                display: false
            }
        },
        {
            label: 'Emergency credit limit',
            data: [],
            backgroundColor: '#C82B2B',
            pointHoverBackgroundColor: '#C82B2B',
            legend: {
                display: true
            }
        });

        let config = this.getChartConfig();

        let chartConfiguration: Chart.ChartConfiguration = {
            type: config.type,
            options: config.options,
            data: { datasets: datasets },


        };

        if (this.debounceId) {
            window.clearTimeout(this.debounceId);
        };

        this.debounceId = window.setTimeout(() => {
            if (this.chart != null) this.chart.destroy();
            let ctx = this.basechart.nativeElement;
            this.chart = new Chart(ctx, chartConfiguration);
        }, this.debounceTimeout);
    }

}
